/**
 * MemoriesPage — /memories
 * Demo page: /file-manager (files as a searchable list with type and owner columns) and /tables/data-tables.
 * Agents write memories with the remember() tool during conversations; people read, search and manage them here.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Brain } from 'lucide-react';
import {
  Badge,
  Button,
  Callout,
  DataTable,
  EmptyState,
  SearchBar,
  Toolbar,
  type DataTableColumn,
} from '../../components/ui/index.js';
import { ListPage } from '../../components/page/index.js';
import { PAGE_HELP } from '../../help/index.js';
import { listMemoriesInfiniteOptions, searchMemoriesOptions } from '../../generated/api/@tanstack/react-query.gen.js';
import type { Memory } from '../../generated/api/types.gen.js';
import { describeQueryError } from '../../components/ui/QueryBoundary.js';
import { FilterSelect } from '../../components/ui/FilterSelect.js';
import {
  formatRelativeTime,
  MEMORY_SCOPE_LABEL,
  MEMORY_SCOPE_VARIANT,
  MEMORY_SCOPES,
  type MemoryScope,
} from './memory-detail/memory-scope.js';

const PAGE_SIZE = 20;

function truncate(text: string, max = 140): string {
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

const COLUMNS: DataTableColumn<Memory>[] = [
  {
    key: 'fact',
    header: 'Fact',
    width: '60%',
    render: (memory) => (
      <Link
        to={`/memories/${memory.id}`}
        className="text-heading transition-colors hover:text-accent-text"
        onClick={(e) => e.stopPropagation()}
      >
        {truncate(memory.factText)}
      </Link>
    ),
  },
  {
    key: 'scope',
    header: 'Scope',
    render: (memory) => <Badge variant={MEMORY_SCOPE_VARIANT[memory.scope]}>{MEMORY_SCOPE_LABEL[memory.scope]}</Badge>,
  },
  {
    key: 'created',
    header: 'Created',
    sortable: true,
    sortValue: (memory) => new Date(memory.createdAt).getTime(),
    render: (memory) => <span className="whitespace-nowrap text-muted">{formatRelativeTime(memory.createdAt)}</span>,
  },
];

export const MemoriesPage: React.FC = () => {
  const help = PAGE_HELP['memories'];
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<MemoryScope | ''>('');
  const term = search.trim();
  const scopeQuery = scope ? { scope } : {};

  const list = useInfiniteQuery({
    ...listMemoriesInfiniteOptions({
      query: { limit: PAGE_SIZE, sort_by: 'createdAt', sort_order: 'desc', ...scopeQuery },
    }),
    initialPageParam: {},
    getNextPageParam: (last) => (last.paging.hasMore ? (last.paging.cursors.after ?? undefined) : undefined),
    enabled: term.length === 0,
  });
  const found = useQuery({
    ...searchMemoriesOptions({ query: { q: term, limit: 50, ...scopeQuery } }),
    enabled: term.length > 0,
  });

  const searching = term.length > 0;
  const rows: Memory[] = searching ? (found.data?.data ?? []) : (list.data?.pages.flatMap((p) => p.data) ?? []);
  const loading = searching ? found.isPending : list.isPending;
  const error = searching ? found.error : list.error;

  const emptyDescription = searching
    ? `No memories match "${term}".`
    : scope
      ? `No memories at ${MEMORY_SCOPE_LABEL[scope].toLowerCase()} scope.`
      : 'Agents write memories with the remember() tool during conversations. Start a conversation to create the first one.';

  return (
    <ListPage
      title="Memories"
      subtitle="Facts agents learned during conversations"
      helpContent={help}
      alert={
        error ? (
          <Callout
            tone="danger"
            title={describeQueryError(error, 'memories')}
            actions={
              <Button size="sm" variant="secondary" onClick={() => (searching ? found.refetch() : list.refetch())}>
                Retry
              </Button>
            }
          />
        ) : undefined
      }
      toolbar={
        <Toolbar
          search={<SearchBar value={search} onChange={setSearch} placeholder="Search memories…" />}
          right={
            <FilterSelect
              label="Scope"
              anyLabel="All scopes"
              value={scope}
              options={MEMORY_SCOPES.map((s) => ({ value: s, label: MEMORY_SCOPE_LABEL[s] }))}
              onChange={(value) => setScope(value as MemoryScope | '')}
            />
          }
        />
      }
      footer={
        !searching && list.hasNextPage ? (
          <div className="flex justify-center">
            <Button variant="secondary" loading={list.isFetchingNextPage} onClick={() => list.fetchNextPage()}>
              Load more
            </Button>
          </div>
        ) : undefined
      }
    >
      <DataTable
        aria-label="Memories"
        columns={COLUMNS}
        rows={rows}
        rowKey={(memory) => memory.id}
        loading={loading}
        onRowClick={(memory) => navigate(`/memories/${memory.id}`)}
        empty={
          <EmptyState
            icon={Brain}
            title={help?.emptyState?.title ?? 'No memories found'}
            description={emptyDescription}
            learnItems={help?.emptyState?.learnItems}
          />
        }
      />
    </ListPage>
  );
};
